// Inline SVG slides share one document, so their ids collide.
//
// An SVG slide is injected into the page markup (not an <img>), which is what
// lets it scale and stay crisp on a 4K panel. The cost is that every id inside
// it — gradients, filters, clip paths — lands in the same document as every
// other slide that happens to be mounted at the same time, and a document may
// only have one element per id. `url(#sky)` resolves to the FIRST `id="sky"` in
// document order, whoever wrote it.
//
// That is not hypothetical. The player keeps the outgoing slide mounted
// underneath the incoming one for the whole of its turn (so the crossfade has
// something to fade over), and the composer hands nearly every slide the same
// obvious names. In the Youth Room playlist the "AI slide" defines a cream
// `#sky` gradient and the "CIR Weekend" slide that follows it paints its
// background with `url(#sky)` — so the Camp Manitoumi retreat slide came up on
// white instead of black, only on that TV, only in that order. The admin grid
// has the same problem more loudly: a dozen cards, all defining `#bg`.
//
// So: rewrite every id to something unique to this rendering before the markup
// reaches the DOM, and rewrite the references along with it. Purely a display
// concern — what is stored stays exactly as it was authored.

/** Ids we rewrite, and the references we rewrite with them. */
const ID_ATTR = /(\sid\s*=\s*)(["'])(.*?)\2/g;

/** Escape a stored id for use inside a RegExp. */
function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Make every id in `svg` unique to `scope`, rewriting the references too.
 *
 * Handles the three ways one part of an SVG points at another: `url(#id)` (with
 * or without quotes) for paint servers, filters, clip paths and masks;
 * `href="#id"` for `<use>`; and the `xlink:href` spelling older tools emit.
 *
 * Unknown reference forms are left alone rather than guessed at — a missed
 * reference renders as it does today, whereas a wrong rewrite would break a
 * slide that currently works.
 */
export function scopeSvgIds(svg: string, scope: string): string {
  if (!svg) return svg;

  // useId() produces ":r3:" and friends. Colons are legal in a fragment, but
  // they make anything downstream that treats the value as a CSS selector
  // fragile for no gain.
  const suffix = scope.replace(/[^A-Za-z0-9_-]/g, "");
  if (!suffix) return svg;

  const ids = new Set<string>();
  for (const m of svg.matchAll(ID_ATTR)) {
    const id = m[3];
    if (id) ids.add(id);
  }
  if (ids.size === 0) return svg;

  let out = svg.replace(ID_ATTR, (whole, lead: string, q: string, id: string) =>
    id ? `${lead}${q}${id}-${suffix}${q}` : whole,
  );

  for (const id of ids) {
    const e = escapeRe(id);
    // url(#id) · url('#id') · url("#id")
    out = out.replace(
      new RegExp(`url\\(\\s*(["']?)#${e}\\1\\s*\\)`, "g"),
      (_m, q: string) => `url(${q}#${id}-${suffix}${q})`,
    );
    // href="#id" and xlink:href="#id"
    out = out.replace(
      new RegExp(`((?:xlink:)?href\\s*=\\s*)(["'])#${e}\\2`, "g"),
      (_m, lead: string, q: string) => `${lead}${q}#${id}-${suffix}${q}`,
    );
  }

  return out;
}
